import React, { useEffect, useState } from "react";
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from "../contexts/UserContext";
import User from '../types/User';
import { SettingsModalProps } from "../types/Props";
import { isValidInput, isValidLength } from "../utils/validateInput";
import { useUserLocation } from '../utils/locationUtils';
import { checkUserSubscription } from '../pushNotifications/subscribeToPushNotifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from "../utils/icons";
import { Link } from "react-router-dom";
import { fetchUserCount } from "../api/userCount";
import { deleteUser, updatePasswordViaOldPassword, updateRadius, updateUsername } from "../api/user";
import FetchError from "../types/Error";
import { ModalWrapper } from "../components/ModalWrapper";
import { isUserAdmin } from "../utils/authUtils";

const SettingsModal = ({ onClose }: SettingsModalProps) => {
  const { language, setLanguage, translations } = useLanguage();
  const { user, setUser } = useUser();
  const [tempUser, setTempUser] = useState<User>(user as User);
  const [isAccountEditMode, setIsAccountEditMode] = useState(false);
  const [isRadiusEditMode, setIsRadiusEditMode] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showRadiusInfo, setShowRadiusInfo] = useState(false);
  const usernamePattern = /^[a-zA-ZäöÄÖ0-9_ -]{3,16}$/;
  const radiusPattern = /^[0-9]*$/;
  const [openSlider, setOpenSlider] = useState<string | null>(null);
  const {userLatitude, userLongitude } = useUserLocation();
  const [notificationStatus, setNotificationStatus] = useState({
    permissionGranted: false,
    endpointMatches: false,
  });
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    const checkNotificationStatus = async () => {
      const permissionGranted = Notification.permission === 'granted';
      const endpointMatches = await checkUserSubscription();

      setNotificationStatus({
        permissionGranted,
        endpointMatches,
      });
    };

    checkNotificationStatus();
  }, [user]);

  useEffect(() => {
    if (!isUserAdmin()) return;

    const getUserCount = async () => {
      const count = await fetchUserCount();
      setUserCount(count);
    };

    getUserCount();
  }, []);

  const toggleAccountEditMode = () => {
    setIsAccountEditMode(!isAccountEditMode);
    setSuccess('');
    setError('');
  };

  const toggleRadiusEditMode = () => {
    setIsRadiusEditMode(!isRadiusEditMode);
    setSuccess('');
    setError('');
  };

  const toggleSlider = (slider: string) => {
    setOpenSlider(openSlider === slider ? null : slider);
    handleCancel();
    setSuccess('');
    setError('');
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowPasswordChange = (toggle: boolean) => {
    setShowChangePassword(toggle);
    setOldPassword('');
    setNewPassword('');
    setPasswordError('');
  };

  const handleSubmitUsername = async () => {
    if (!isValidLength(tempUser.user_name, 3, 16)) {
      setError(translations.errors.invalidusernamelength);
      return;
    };

    if (!isValidInput(tempUser.user_name, usernamePattern)) {
      setError(translations.errors.invalidusernamechars);
      return;
    };

    try {
      const data = await updateUsername(tempUser.user_name);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setIsAccountEditMode(false);
      setError('');
      setSuccess(translations.successes.settingsupdated);
    } catch (error) {
      const err = error as FetchError;
      if (err.message === 'Username already taken') {
        setError(translations.errors.usernameexists)
      } else {
      setError(translations.errors.genericerror);
      }
    }
  };

  const handleSubmitRadius = async () => {
    if (!isValidLength(tempUser.radius.toString(), 1, 3)) {
      setError(translations.errors.invalidradiuslength);
      return;
    };

    if (!isValidInput(tempUser.radius.toString(), radiusPattern)) {
      setError(translations.errors.invalidradiuschars);
      return;
    };

    try {
      const data = await updateRadius(tempUser.radius);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setIsRadiusEditMode(false);
      setError('');
      setSuccess(translations.successes.settingsupdated);
    } catch (error) {
      setError(translations.errors.genericerror);
    }
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 5) {
      setPasswordError(translations.errors.passwordtooshort);
      return;
    }

    try {
      await updatePasswordViaOldPassword(oldPassword, newPassword);
      setNewPassword('');
      setShowChangePassword(false);
      setPasswordError('');
      setSuccess(translations.successes.settingsupdated);
    } catch (error) {
      const err = error as FetchError;
      if (err.message === 'Incorrect password') {
        setPasswordError(translations.errors.invalidpassword)
      } else {
        setPasswordError(translations.errors.genericerror);
      }
    }
  };

  const handleCancel = () => {
    if (user) setTempUser(user);
    setIsAccountEditMode(false);
    setIsRadiusEditMode(false);
    setError('');
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/user/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout failed', err);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);

    window.location.reload();
  };

  const handleDelete = async () => {
    try {
      await deleteUser();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.reload();
    } catch (error) {
      setError(translations.errors.genericerror);
    }
  };

  return (
    <ModalWrapper title={translations.settingspage.settings} onClose={onClose}>
      <ul>
        { user &&
          <>
            <li className="card list-item">
              <div className="list-header" onClick={() => toggleSlider('account')}>
                <FontAwesomeIcon icon={icons.user} className="icon-left"/> {translations.settingspage.account}
                <FontAwesomeIcon icon={openSlider === 'account' ? icons.chevronDown : icons.chevronRight} className="icon-right"/>
              </div>
              <div className={`settings-slider ${openSlider === 'account' ? 'show' : ''}`}>
                <label>
                  <div className="spacer2"><FontAwesomeIcon icon={icons.signature}/> {translations.username}:</div>
                  <input
                    className={`text-input ${isAccountEditMode ? 'active-edit' : 'inactive-edit'}`}
                    type="text"
                    name="usernamefield"
                    value={tempUser.user_name}
                    disabled={!isAccountEditMode}
                    onChange={(e) => setTempUser({ ...tempUser, user_name: e.target.value })}
                  />
                </label>
                { isAccountEditMode ? (
                  <>
                    <button
                      type="button"
                      className="big-button settings-save"
                      onClick={handleSubmitUsername}
                    >
                      <FontAwesomeIcon icon={icons.floppyDisk}/> {translations.save}
                    </button>
                    <button
                      type="button"
                      className="big-button settings-cancel"
                      onClick={handleCancel}
                    >
                      <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="big-button settings-edit"
                    onClick={toggleAccountEditMode}
                  >
                    <FontAwesomeIcon icon={icons.penToSquare}/> {translations.edit}
                  </button>
                )}
                <label>
                  <div className="spacer2"><FontAwesomeIcon icon={icons.phone}/> {translations.phone}:</div>
                  <input 
                    className="text-input settings-phone inactive-edit"
                    type="text" 
                    name="phonefield" 
                    value={user.phone} 
                    disabled
                  />
                </label>
                {success && <div className="success">{success}</div>}
                {error && <div className="error">{error}</div>}<br />
                <button type="button" className="big-button password-change" onClick={() => toggleShowPasswordChange(true)}>
                  <FontAwesomeIcon icon={icons.lock}/> {translations.changepassword}
                </button><br />
                <button type="button" className="big-button account-delete" onClick={() => setShowDeleteConfirmation(true)}>
                  <FontAwesomeIcon icon={icons.trash}/> {translations.settingspage.deleteaccount}
                </button>
              </div>
            </li>
            <li className="card list-item">
              <div className="list-header" onClick={() => toggleSlider('radius')}>
                <FontAwesomeIcon icon={icons.locationDot} className="icon-left"/> {translations.settingspage.radiusandlocation}
                <FontAwesomeIcon icon={openSlider === 'radius' ? icons.chevronDown : icons.chevronRight} className="icon-right"/>
              </div>
              <div className={`settings-slider ${openSlider === 'radius' ? 'show' : ''}`}>
                <label>
                  <div className="radius-container">
                    <FontAwesomeIcon icon={icons.map} className="listIcon"/> {translations.radius}: 
                    <div className="radius-info" onClick={() => setShowRadiusInfo(!showRadiusInfo)}>
                      {translations.whatisradius}
                    </div>
                  </div>
                  <div className={`radius-info-popup ${showRadiusInfo ? 'show' : ''}`}>
                    <p>{translations.radiusinfosettings}</p>
                  </div>
                  <div className="radius-input-container">
                    <input 
                      className={`text-input radius ${isRadiusEditMode ? 'active-edit' : 'inactive-edit'}`}
                      type="number" 
                      value={tempUser.radius}
                      disabled={!isRadiusEditMode}
                      onChange={(e) => setTempUser({ ...tempUser, radius: parseInt(e.target.value) })}
                    />
                    <span className="radius-unit">{translations.km}</span>
                    { isRadiusEditMode ? (
                      <>
                        <button
                          type="button"
                          className="big-button settings-save"
                          onClick={handleSubmitRadius}
                        >
                          <FontAwesomeIcon icon={icons.floppyDisk}/> {translations.save}
                        </button>
                        <button
                          type="button"
                          className="big-button settings-cancel"
                          onClick={handleCancel}
                        >
                          <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="big-button settings-edit"
                        onClick={toggleRadiusEditMode}
                      >
                        <FontAwesomeIcon icon={icons.penToSquare}/> {translations.edit}
                      </button>
                    )}
                  </div>
                  {success && <div className="success">{success}</div>}
                  {error && <div className="error">{error}</div>}
                  <div>
                    {userLatitude && userLongitude ? (
                      <div>
                        <span className="text-spacer">
                          <FontAwesomeIcon icon={icons.locationDot} className="icon-left"/> {translations.settingspage.ownlocationinfo}
                        </span>
                        <span className="success">
                          {translations.settingspage.inuse}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className="text-spacer">
                          <FontAwesomeIcon icon={icons.locationDot} className="icon-left"/> {translations.settingspage.ownlocationinfo}
                        </span>
                        <span className="error">
                          {translations.settingspage.notinuse}
                        </span><br />
                        {translations.settingspage.ownlocationinfo2}
                      </div>
                    )}  
                  </div>
                </label>
              </div>
            </li>
            <li className="card list-item">
              <div className="list-header" onClick={() => toggleSlider('notifications')}>
                <FontAwesomeIcon icon={icons.bell} className="icon-left"/> {translations.settingspage.notifications}
                <FontAwesomeIcon icon={openSlider === 'notifications' ? icons.chevronDown : icons.chevronRight} className="icon-right"/>
              </div>
              <div className={`settings-slider ${openSlider === 'notifications' ? 'show' : ''}`}>
                {notificationStatus.permissionGranted && notificationStatus.endpointMatches ? (
                  <div>
                    <span className="text-spacer">
                      {translations.settingspage.notifications}
                    </span>
                    <span className="success">
                      {translations.settingspage.on}
                    </span>
                  </div>
                ) : (
                  <div>
                    <span className="text-spacer">
                      {translations.settingspage.notifications}
                    </span>
                    <span className="error">
                      {translations.settingspage.off}
                    </span><br />
                    {translations.settingspage.notificationsinfo}
                  </div>
                )}
              </div>
            </li>
          </>
        }
        <li className="card list-item">
          <div className="list-header" onClick={() => toggleSlider('language')}>
            <FontAwesomeIcon icon={icons.language} className="icon-left"/> {translations.settingspage.language}
            <FontAwesomeIcon icon={openSlider === 'language' ? icons.chevronDown : icons.chevronRight} className="icon-right"/>
          </div>
          <div className={`settings-slider ${openSlider === 'language' ? 'show' : ''}`}>
            <div className="select-wrapper spacer3">
              <select 
                name="language" 
                className="dropdown"
                value={language} 
                onChange={(e) => setLanguage(e.target.value)} 
              >
                <option value="english">English</option>
                <option value="finnish">Suomi</option>
                <option value="swedish">Svenska</option>
              </select>
            </div>
          </div>
        </li>
        <li className="card list-item">
          <Link to="/info" className="list-header infopage-link">
            <FontAwesomeIcon icon={icons.cirlceQuestion} className="icon-left"/> {translations.settingspage.info}
            <FontAwesomeIcon icon={icons.arrowUpRightFromSquare} className="icon-right"/>
          </Link>
        </li>
        { user ? (
          <li className="card list-item logout" onClick={() => setShowLogoutConfirmation(true)}>
            <div className="list-header">
            <FontAwesomeIcon icon={icons.rightFromBracket} className="icon-left"/> {translations.settingspage.logout}
            </div>
          </li>
        ) : (
          <Link to="/login">
            <span>{translations.login}</span>
          </Link>
        )}
      </ul>
      { showLogoutConfirmation &&
      <div className="confirmation-overlay">
        <div className="card confirm-card">
          <p>{translations.settingspage.confirmlogout}</p>
          <div className="confirmation-buttons">
            <button className="big-button confirmation-cancel grey" onClick={() => setShowLogoutConfirmation(false)}>
              <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
            </button>
            <button className="big-button confirmation-confirm warning" onClick={handleLogout}>
              <FontAwesomeIcon icon={icons.rightFromBracket}/> {translations.settingspage.logout}
            </button>
          </div>
        </div>
      </div>
      }
      { showDeleteConfirmation &&
        <div className="confirmation-overlay">
          <div className="card confirm-card">
            <p>{translations.settingspage.confirmaccountdelete}</p>
            <div className="confirmation-buttons">
              <button className="big-button confirmation-cancel grey" onClick={() => setShowDeleteConfirmation(false)}>
              <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
              </button>
              <button className="big-button confirmation-confirm warning" onClick={handleDelete}>
              <FontAwesomeIcon icon={icons.trash}/> {translations.delete}
              </button>
            </div>
          </div>
        </div>
      }
      { showChangePassword &&
        <div className="confirmation-overlay">
          <form className="card confirm-card new-password" onSubmit={handleSubmitNewPassword}>
            <label>
            {translations.settingspage.currentpassword}:
              <div className="input-container-password">
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  className="auth-input"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder={translations.settingspage.currentpassword}
                  required
                />
                <FontAwesomeIcon
                  icon={showOldPassword ? icons.eyeSlash : icons.eye}
                  className="password-icon"
                  onClick={toggleOldPasswordVisibility}
                />
              </div>
            </label>
            <label>
            {translations.newpassword}: <span className="password-info">{translations.min5chars}</span>
              <div className="input-container-password">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="auth-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={translations.newpassword}
                  required
                />
                <FontAwesomeIcon
                  icon={showNewPassword ? icons.eyeSlash : icons.eye}
                  className="password-icon"
                  onClick={toggleNewPasswordVisibility}
                />
              </div>
            </label>
            {passwordError && <div className="error">{passwordError}</div>}
            <div className="confirmation-buttons">
              <button 
                type="button"
                className="big-button confirmation-cancel-red" 
                onClick={() => toggleShowPasswordChange(false)}
              >
                <FontAwesomeIcon icon={icons.xmark}/> {translations.cancel}
              </button>
              <button 
                className="big-button confirmation-confirm" 
                type="submit"
              >
                <FontAwesomeIcon icon={icons.check}/> {translations.changepassword}
              </button>
            </div>
          </form>
        </div>
      }
      { isUserAdmin() &&
        <div className="user-count">
          {userCount !== null ? <p><FontAwesomeIcon icon={icons.user}/> {userCount} käyttäjää</p> : <p>{translations.loading}</p>}
        </div>
      }
    </ModalWrapper>
  );
}

export default SettingsModal;